import { type ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RiExpandDiagonalLine } from '@remixicon/react';

import { useRouteRef } from '@backstage/core-plugin-api';

import type { CsrPlayerElement } from '@spotify-internal/csr-player';
import {
  ButtonIcon,
  Flex,
  ProgressCircle,
  Text,
} from '@spotify-internal/encore-web';
import {
  CAROUSEL_ICON_SIZE,
  Carousel,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';

import { formatRecordingTime } from '../helpers';
import { useRecording } from '../hooks';
import { RecordingPlayer } from '../recording';
import { getRecordingDownload } from '../recording/getRecordingDownload';
import { recordingRouteRef } from '../routes';
import { type RecordingHit, getRecordingKey } from './types';

import styles from './RecordingsResult.module.css';

type RecordingPreviewCardProps = {
  recording: RecordingHit;
  onOpenFullView: () => void;
  onPlayerReady: (player: CsrPlayerElement) => void;
};

const RecordingPreviewCard = ({
  recording,
  onOpenFullView,
  onPlayerReady,
}: RecordingPreviewCardProps) => {
  const [playerError, setPlayerError] = useState<Error | undefined>();
  const {
    recording: recordingDetails,
    isLoading,
    error: recordingError,
  } = useRecording({
    name: recording.name,
    fetchPolicy: 'network-only',
  });
  const { downloadUri, downloadError } = getRecordingDownload(recordingDetails);
  const error = recordingError ?? downloadError ?? playerError;
  const createTime = recordingDetails?.createTime
    ? formatRecordingTime(recordingDetails.createTime)
    : undefined;
  const recordingLabel =
    recording.display_name || recordingDetails?.displayName || recording.name;

  const handlePlayerReady = (player: CsrPlayerElement) => {
    if (recording.startTime !== undefined && Number.isFinite(player.duration)) {
      player.currentTime = Math.min(recording.startTime, player.duration);
    }
    if (recording.endTime !== undefined) {
      const { endTime } = recording;
      const onTimeUpdate = () => {
        if (player.currentTime >= endTime) {
          player.pause();
          player.removeEventListener('timeupdate', onTimeUpdate);
        }
      };
      player.addEventListener('timeupdate', onTimeUpdate);
    }
    onPlayerReady(player);
  };

  let playerContent: ReactNode;

  if (isLoading) {
    playerContent = (
      <div className={styles.playerStatus}>
        <ProgressCircle size="small" aria-label="Loading recording preview" />
      </div>
    );
  } else if (error || !downloadUri) {
    playerContent = (
      <div className={styles.playerStatus}>
        <Text as="p" variant="bodySmall" semanticColor="textNegative">
          Unable to load recording preview
        </Text>
      </div>
    );
  } else {
    playerContent = (
      <RecordingPlayer
        src={downloadUri}
        variant="simplified"
        autoplay={false}
        initialPageUrl={recordingDetails?.routing?.entryPage ?? undefined}
        onPlayerReady={handlePlayerReady}
        onError={setPlayerError}
      />
    );
  }

  return (
    <article
      aria-label={recordingLabel}
      className={styles.recordingCard}
      data-track-scope="region.recording-preview"
      data-track-data-recording={recording.name}
    >
      <Flex direction="column">
        <header className={styles.recordingHeader}>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex direction="column">
              <Text as="p" variant="bodyMediumBold" lineClamp={1}>
                {recordingLabel}
              </Text>
              {createTime && (
                <Text
                  as="p"
                  variant="marginal"
                  semanticColor="textSubdued"
                  lineClamp={1}
                >
                  {createTime}
                </Text>
              )}
            </Flex>
            <ButtonIcon
              variant="tertiary"
              size="small"
              aria-label="Open full view"
              onClick={onOpenFullView}
              data-track-id="button.open-full-view"
              data-track-action="navigate"
            >
              <RiExpandDiagonalLine size={CAROUSEL_ICON_SIZE} aria-hidden />
            </ButtonIcon>
          </Flex>
        </header>
        <div className={styles.playerContainer}>{playerContent}</div>
      </Flex>
    </article>
  );
};

export const RecordingsResult = ({
  recordings,
}: {
  recordings: RecordingHit[];
}) => {
  const navigate = useNavigate();
  const recordingRoute = useRouteRef(recordingRouteRef);
  const playersRef = useRef(new Map<string, CsrPlayerElement>());

  if (recordings.length === 0) {
    return null;
  }

  const openFullView = (recording: RecordingHit) => {
    const recordingId =
      extractLastNameComponent(recording.name) ?? recording.name;
    const recordingHref = recordingRoute({ id: recordingId });
    const currentTime = playersRef.current.get(
      getRecordingKey(recording),
    )?.currentTime;

    navigate(currentTime ? `${recordingHref}?t=${currentTime}` : recordingHref);
  };

  return (
    <Carousel
      className={styles.carousel}
      ariaLabel="Recording carousel"
      itemName="recording"
    >
      {recordings.map(carouselRecording => (
        <RecordingPreviewCard
          key={getRecordingKey(carouselRecording)}
          recording={carouselRecording}
          onOpenFullView={() => openFullView(carouselRecording)}
          onPlayerReady={player => {
            playersRef.current.set(getRecordingKey(carouselRecording), player);
          }}
        />
      ))}
    </Carousel>
  );
};
